import { useEffect, useRef, useState } from 'react'
import { api } from '../lib/api.js'
import { startChangeFeed, stopChangeFeed, onChange } from '../lib/changes.js'
import './monitor.css'
import { formatBeijingTime } from '../lib/time.js'
import { visibleSyncErrors, retryableSyncJobs, retrySyncJobs } from '../lib/monitorSync.js'
import { visibleMonitorTeam } from '../lib/monitorTeam.js'

// 监控大屏（2026-10-04 Gavin 要，只给管理员看）。三层：
//   探活 —— 服务活着、消息在进来；报错 —— 坏了但不一定报错的事；情况 —— 现在有没有人等着处理。
// 两个入口共用这一页：后台左栏「监控大屏」，和不套左栏的单独网页 #/screen（standalone）。
// 单独网页不发心跳：开在电视或另一个窗口上，不会让管理员账号变「在线」被派单。

const POLL_MS = 15 * 1000     // 等待时长在走、心跳掉线不会发变化通知，定时补拉
const STALE_SEC = 45          // 这么久没拿到新数据，整页变灰：大屏最怕卡死了还显示着旧的绿灯

const SHIFT_NAME = { day: '白班', mid: '中班', night: '晚班', off: '休息' }
const PLAN_NAME = { 360: '360', 180: '180', 158: '158', credit: '信用卡' }

const ago = (t, now) => t ? Math.max(0, (now - new Date(t).getTime()) / 60000) : null
const fmtMin = m => m == null ? '—' : m < 1 ? '刚刚' : m < 60 ? `${Math.floor(m)} 分钟` : m < 1440 ? `${Math.floor(m / 60)} 小时 ${Math.floor(m % 60)} 分` : `${Math.floor(m / 1440)} 天`
const fmtTok = n => n >= 1e6 ? `${(n / 1e6).toFixed(2)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}K` : String(n || 0)
const pct = (a, b) => b > 0 ? `${Math.round(a / b * 100)}%` : '—'
// 等待时长着色：3 分钟黄、5 分钟红（2026-10-04 方案）
const waitTone = m => m >= 5 ? 'bad' : m >= 3 ? 'warn' : ''

export default function Monitor({ standalone }) {
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [okAt, setOkAt] = useState(0)
  const [now, setNow] = useState(Date.now())
  const [retrying, setRetrying] = useState(null)
  const [retryResult, setRetryResult] = useState(null)
  const retryBusy = useRef(false)
  const alive = useRef(false)
  const lifecycle = useRef(0)
  const loadVersion = useRef(0)

  async function retrySync(jobs, single = false) {
    const targets = retryableSyncJobs(jobs)
    if (retryBusy.current || !alive.current || !targets.length) return
    retryBusy.current = true // 同一事件循环内双击也不能重复提交
    loadVersion.current++ // 作废重试前已发出的轮询响应
    const generation = lifecycle.current
    const active = () => alive.current && lifecycle.current === generation
    setRetrying({ id: single ? targets[0].id : null, done: 0, total: targets.length })
    setRetryResult(null)
    try {
      const result = await retrySyncJobs(targets, api.retryOrderSyncJob, {
        shouldContinue: active,
        onProgress: progress => { if (active()) setRetrying(s => ({ ...s, ...progress })) },
      })
      if (!active()) return
      setRetryResult(result)
      // 刷新失败时也移除已确认入队的项，避免拿旧列表再次提交。
      const failedIds = new Set(result.failures.map(f => f.id))
      const submittedIds = new Set(targets.slice(0, result.total - result.skipped).filter(j => !failedIds.has(j.id)).map(j => j.id))
      setData(d => d?.orderSync ? { ...d, orderSync: { ...d.orderSync, errors: d.orderSync.errors?.filter(j => !submittedIds.has(j.id)) } } : d)
      const version = ++loadVersion.current
      try {
        const fresh = await api.getMonitor()
        if (active() && version === loadVersion.current) { setData(fresh); setErr(''); setOkAt(Date.now()) }
      } catch (e) {
        if (active()) { setErr(e.message); setRetryResult(r => ({ ...r, refreshError: e.message })) }
      }
    } finally {
      retryBusy.current = false
      if (active()) setRetrying(null)
    }
  }

  useEffect(() => {
    alive.current = true
    const generation = ++lifecycle.current
    const active = () => alive.current && lifecycle.current === generation
    let busy = false   // 上一次还没回来就不再发，变化通知密集时不叠请求
    const load = () => {
      if (busy || retryBusy.current) return
      busy = true
      const version = ++loadVersion.current
      api.getMonitor()
        .then(d => { if (active() && version === loadVersion.current) { setData(d); setErr(''); setOkAt(Date.now()) } })
        .catch(e => { if (active() && version === loadVersion.current) setErr(e.message) })
        .finally(() => { busy = false })
    }
    load()
    // 后台里由 Layout 挂着变化通知；单独网页没有 Layout，自己挂
    if (standalone) startChangeFeed()
    const off = onChange(load)
    const timer = setInterval(load, POLL_MS)
    const clock = setInterval(() => setNow(Date.now()), 1000)
    return () => {
      alive.current = false; lifecycle.current++; loadVersion.current++
      off(); clearInterval(timer); clearInterval(clock)
      if (standalone) stopChangeFeed()
    }
  }, [standalone])

  const staleSec = okAt ? Math.floor((now - okAt) / 1000) : 0
  const stale = !!okAt && staleSec > STALE_SEC

  if (!data) {
    return <div className={`mon${standalone ? ' mon-full' : ''}`}><div className="mon-empty">{err || '加载中…'}</div></div>
  }

  const { counts, queue, overdue, aiStuck, risks, verify, team: rawTeam, today, runtime: rt, system: sys } = data
  const team = visibleMonitorTeam(rawTeam)
  const syncErrors = visibleSyncErrors(data.orderSync?.errors)
  const retryableJobs = retryableSyncJobs(syncErrors)
  // 页面上的「多久以前」都按服务器时间算，避免电视 / 电脑本地时钟不准
  const at = new Date(data.serverTime).getTime() + (now - okAt)
  const since = t => ago(t, at)

  // ---------- 探活 ----------
  const lastUserMin = since(today.last_user_at)
  const main = sys.procs?.find(p => p.name === 'etc-server')
  const others = (sys.procs || []).filter(p => p.name !== 'etc-server')
  const memUsed = sys.host ? 1 - sys.host.memAvail / sys.host.memTotal : null
  const diskUsed = sys.host?.diskTotal ? 1 - sys.host.diskFree / sys.host.diskTotal : null
  const backupH = sys.backup ? since(sys.backup.at) / 60 : null
  const certDays = sys.certs?.length ? Math.floor((new Date(sys.certs[0].expires).getTime() - at) / 86400000) : null
  const probes = [
    {
      name: '客户消息', tone: data.working && (lastUserMin == null || lastUserMin > 60) ? 'warn' : 'ok',
      value: lastUserMin == null ? '还没有' : `${fmtMin(lastUserMin)}前`,
      sub: `近 15 分钟 ${today.user_15m} 条`,
      tip: '最后一条客户消息的时间。企微推送断了不会报错，只是再也没有消息进来——上班时间长时间为 0 要去查',
    },
    {
      name: '主服务', tone: !main ? 'warn' : main.status !== 'online' ? 'bad' : rt.lastExit === 'unexpected' ? 'warn' : 'ok',
      value: main ? (main.status === 'online' ? `已运行 ${fmtMin(main.uptimeMs / 60000)}` : main.status) : '查不到',
      sub: main ? `${rt.lastExit === 'unexpected' ? '上次是意外退出 · ' : ''}重启 ${main.restarts} 次 · ${Math.round(main.memory / 1048576)}M` : '',
      tip: '上次退出：正常停（部署）还是意外退出（崩溃 / 被杀）',
    },
    ...others.map(p => ({
      name: p.name === 'etc-aftersales-server' ? '售后后台' : p.name === 'kf-livecode' ? '客服活码' : p.name,
      tone: p.status === 'online' ? 'ok' : 'bad',
      value: p.status === 'online' ? `已运行 ${fmtMin(p.uptimeMs / 60000)}` : p.status,
      sub: `重启 ${p.restarts} 次 · ${Math.round(p.memory / 1048576)}M`,
    })),
    { name: '数据库', tone: rt.dbMs > 500 ? 'warn' : 'ok', value: `${rt.dbMs}ms`, sub: '查询耗时' },
    {
      name: '模型', tone: rt.llmFail > 0 ? 'bad' : rt.llmFallback > 0 || rt.llmP90Ms > 10000 ? 'warn' : 'ok',
      value: rt.llmAvgMs == null ? '本次启动后还没调用' : `平均 ${(rt.llmAvgMs / 1000).toFixed(1)}s`,
      sub: rt.llmP90Ms == null ? `接入：${rt.wecomMode === 'standalone' ? '独立' : '融合'}` : `P90 ${(rt.llmP90Ms / 1000).toFixed(1)}s`,
      tip: '本次服务启动以来 AI 回复的耗时，重启会清零（不是今天全天的用量，今天的用量见下面「模型用量」表）；P90 = 最慢的 10% 从多少秒起',
    },
    {
      name: '服务器', tone: memUsed > 0.9 || diskUsed > 0.85 ? 'bad' : memUsed > 0.8 || diskUsed > 0.7 ? 'warn' : 'ok',
      value: memUsed == null ? '—' : `内存 ${Math.round(memUsed * 100)}%`,
      sub: `磁盘 ${diskUsed == null ? '—' : Math.round(diskUsed * 100) + '%'} · 负载 ${sys.host?.load?.toFixed(2) ?? '—'}`,
    },
    {
      name: '备份', tone: backupH == null ? 'warn' : backupH > 26 ? 'bad' : 'ok',
      value: backupH == null ? '查不到' : `${fmtMin(backupH * 60)}前`,
      sub: sys.backup ? `数据库 ${Math.round(sys.backup.size / 1024)}K · 每天 11:30` : '',
    },
    {
      name: '证书', tone: certDays == null ? 'warn' : certDays < 15 ? 'bad' : certDays < 30 ? 'warn' : 'ok',
      value: certDays == null ? '查不到' : `${certDays} 天后到期`,
      // 域名太长会换行撑高这张卡（跟别的卡不一样高），只显示子域名那一段，完整域名放悬停提示里
      sub: sys.certs?.[0]?.domain?.split('.')[0] || '',
      tip: (sys.certs || []).map(c => `${c.domain}：${formatBeijingTime(c.expires, { year: 'numeric', month: '2-digit', day: '2-digit' })}`).join('\n'),
    },
  ]

  // ---------- 报错 ----------
  const voidBase = rt.voided + rt.aiSent
  const voidRate = voidBase ? rt.voided / voidBase : 0
  const errors = [
    ...(data.orderSync?.enabled ? [{
      name: '订单同步异常', n: syncErrors.length + (data.orderSync.workerError ? 1 : 0), unit: '项',
      tone: syncErrors.length || data.orderSync.workerError ? 'bad' : 'ok',
      sub: `待同步 ${data.orderSync.pending} · 未绑定 ${data.orderSync.unbound} · 历史待核对 ${data.orderSync.pendingReviews || 0}`,
    }] : []),
    {
      name: '客户没收到的回复', n: today.undelivered.length, unit: '条', tone: today.undelivered.length ? 'bad' : 'ok',
      sub: '企微没收下，今天', tip: '企微返回错误码，客户没收到；系统已留言让人工处理',
    },
    {
      name: 'AI 回复作废', n: voidBase ? `${Math.round(voidRate * 100)}%` : '—', unit: '',
      tone: voidBase >= 5 && voidRate > 0.3 ? 'bad' : rt.voided ? 'warn' : 'ok',
      sub: `${rt.voided} / ${voidBase} 次 · 本次启动以来`,
      tip: '生成期间客户又发了消息、或被接管，旧回复作废。偶尔有正常；比例突然很高说明判断出错了（10-04 出过一次全作废）',
    },
    { name: 'AI 不可用转人工', n: today.ai_down, unit: '次', tone: today.ai_down ? 'bad' : 'ok', sub: '今天' },
    {
      name: '模型换备用 / 全失败', n: `${rt.llmFallback} / ${rt.llmFail}`, unit: '',
      tone: rt.llmFail ? 'bad' : rt.llmFallback ? 'warn' : 'ok', sub: `共 ${rt.llmOk + rt.llmFail} 次 · 本次启动以来`,
    },
    { name: 'AI 卡住没回', n: aiStuck.length, unit: '通', tone: aiStuck.length ? 'bad' : 'ok', sub: '客户说完 2 分钟 AI 没回' },
  ]

  // ---------- 情况 ----------
  const onDuty = team.filter(a => a.online && a.accepting)
  const shouldBe = team.filter(a => a.inShift)
  const absent = team.filter(a => a.inShift && !a.online)
  const longest = queue[0]?.waitMin
  const open = counts.ai + counts.waiting_human + counts.human

  // 待处理：所有「有人在等」的单放一张表，等最久的在上面
  const todo = [
    ...queue.map(c => ({ ...c, kind: '待接入', kindTone: 'warn' })),
    ...overdue.map(c => ({ ...c, kind: '人工没回', kindTone: 'bad' })),
    ...aiStuck.map(c => ({ ...c, kind: 'AI 没回', kindTone: 'bad' })),
    ...today.undelivered.map(u => ({ id: u.convId + u.at, customer: u.customer, waitMin: since(u.at), kind: `没送达 ${u.code.split(' ')[0]}`, kindTone: 'bad' })),
  ].sort((a, b) => b.waitMin - a.waitMin)

  const tokens = today.llm.reduce((s, r) => s + r.prompt + r.completion, 0)
  const llmTotal = today.llm.reduce((t, r) => ({ calls: t.calls + (r.calls || 0), prompt: t.prompt + (r.prompt || 0), completion: t.completion + (r.completion || 0), cache_hit: t.cache_hit + (r.cache_hit || 0) }), { calls: 0, prompt: 0, completion: 0, cache_hit: 0 })

  return (
    <div className={`mon${standalone ? ' mon-full' : ''}${stale ? ' mon-stale' : ''}`}>
      <header className="mon-head">
        <div>
          <span className="mon-title">运营系统监控平台</span>
          <span className={`mon-live ${stale ? 'bad' : err ? 'warn' : 'ok'}`}>
            {stale ? `数据已 ${staleSec} 秒没更新` : err ? `刷新失败：${err}` : `实时 · ${staleSec} 秒前更新`}
          </span>
        </div>
        <div className="mon-actions">
          <span className="mon-clock">{formatBeijingTime(at, { hour12: false, month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
          {standalone
            ? <button className="btn btn-sm" onClick={() => document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen()}>全屏</button>
            : <button className="btn btn-sm" onClick={() => window.open(`${location.pathname}#/screen`, '_blank')}>单独打开</button>}
        </div>
      </header>

      <section className="mon-layer">
        <div className="mon-layer-name">系统健康</div>
        <div className="mon-probes">
          {probes.map(p => (
            <div key={p.name} className={`mon-probe ${p.tone}`} title={p.tip}>
              <div className="mon-probe-name"><i className="dot" />{p.name}</div>
              <div className="mon-probe-val">{p.value}</div>
              <div className="mon-sub">{p.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {data.statisticsCoverage && !data.statisticsCoverage.complete && <div role="status" className="err">
        历史待核对 {data.statisticsCoverage.totalPendingConversations} 条会话，已知日期涉及今天 {data.statisticsCoverage.periodAffectedConversations} 条。
        日统计与报表均为已核实数据，待历史补齐。
      </div>}
      {data.orderSync?.reviews?.length > 0 && <details className="mon-panel">
        <summary>历史待核对（{data.orderSync.reviews.length} 条）</summary>
        <div style={{ overflowX: 'auto' }}><table className="table">
          <thead><tr><th>车牌</th><th>匹配状态</th><th>待核对项</th></tr></thead>
          <tbody>{data.orderSync.reviews.map(r => <tr key={r.convId}>
            <td>{r.plates.join(' / ') || '未提供车牌'}</td>
            <td>{r.category === 'statistics' ? '归属/支付已匹配' : '暂未匹配'}</td>
            <td>{r.issues.join('；')}</td>
          </tr>)}</tbody>
        </table></div>
      </details>}
      <section className="mon-layer">
        <div className="mon-layer-name">报错预警</div>
        <div className="mon-errors">
          {errors.map(e => (
            <div key={e.name} className={`mon-err ${e.tone}`} title={e.tip}>
              <div className="mon-sub">{e.name}</div>
              <div className="mon-err-n">{e.n}<small>{e.unit}</small></div>
              <div className="mon-sub">{e.sub}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="mon-layer">
        <div className="mon-layer-name">实时情况</div>
        <div className="mon-kpis">
          <Kpi name="进行中会话" value={open} sub={`AI ${counts.ai} · 人工 ${counts.human}`} />
          <Kpi name="待接入" value={queue.length} tone={waitTone(longest)} sub={queue.length ? `最长等 ${fmtMin(longest)}` : '没人在等'} />
          <Kpi name="人工没回" value={overdue.length} tone={overdue.length ? 'bad' : ''} sub="客户说完超 3 分钟" />
          <Kpi name="风险单" value={risks.length} tone={risks.length ? 'warn' : ''}
            sub={risks.length ? [...new Set(risks.map(r => r.risk))].join(' · ') : '无'} />
          <Kpi name="待核实付款" value={verify.length} tone={verify.length ? 'warn' : ''} sub="客户说付了，还没标结果" />
          <Kpi name="接单中 / 应在班" value={`${onDuty.length} / ${shouldBe.length}`} tone={absent.length ? 'bad' : ''}
            sub={absent.length ? `缺勤：${absent.map(a => a.name).join('、')}` : '在班的都在线'} />
          <Kpi name="今日成交" value={today.won} tone="good"
            sub={`成功率 ${today.rate == null ? '—' : Math.round(today.rate * 100) + '%'} · 有效进线 ${today.effective}`} />
        </div>

        <div className="mon-grid">
          <div className="mon-card">
            <div className="mon-card-title">等着处理的客户 <b>{todo.length}</b></div>
            {todo.length ? (
              <table className="mon-table">
                <tbody>
                  {todo.slice(0, 12).map(c => (
                    <tr key={c.kind + c.id}>
                      <td><span className={`tag ${c.kindTone}`}>{c.kind}</span></td>
                      <td>{c.customer}{c.risk && <span className="tag warn">{c.risk}</span>}</td>
                      <td className="mon-sub">{c.agent || c.owner || '无归属'}</td>
                      <td className={`num ${waitTone(c.waitMin)}`}>等 {fmtMin(c.waitMin)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="mon-ok-big">没有客户在等</div>}
            {todo.length > 12 && <div className="mon-sub">还有 {todo.length - 12} 个没列出</div>}
            {verify.length > 0 && (
              <div className="mon-note">待核实付款：{verify.map(v => `${v.customer}（${v.owner || '无归属'}）`).join('、')}</div>
            )}
          </div>

          <div className="mon-card">
            <div className="mon-card-title">运营在岗 <b>{onDuty.length}</b> 人接单中</div>
            <table className="mon-table">
              <tbody>
                {team.map(a => {
                  const st = !a.online ? (a.inShift ? ['缺勤', 'bad'] : ['离线', 'off']) : a.accepting ? ['接单中', 'ok'] : ['暂离', 'warn']
                  const load = a.cap ? a.serving / a.cap : 0
                  return (
                    <tr key={a.id}>
                      <td>{a.name}{a.admin && <span className="mon-sub"> 管理员</span>}</td>
                      <td><span className={`tag ${st[1]}`}>{st[0]}</span></td>
                      <td className="mon-sub">{a.shift ? SHIFT_NAME[a.shift] || a.shift : '没排班'}</td>
                      <td className="mon-load-cell">
                        <div className="mon-bar"><i className={load >= 1 ? 'bad' : load >= 0.8 ? 'warn' : ''} style={{ width: `${Math.min(100, load * 100)}%` }} /></div>
                        <span className="num">{a.serving}/{a.cap}</span>
                      </td>
                      <td className="num mon-sub">接待 {a.today} · 成交 {a.success}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="mon-card">
            <div className="mon-card-title">今日转化</div>
            <div className="mon-conv">
              <div><div className="mon-sub">有效进线</div><b>{today.effective}</b><div className="mon-sub">总进线 {today.totalIn} · {data.orderSync?.enabled ? '售后及曾支付' : '之前已成交'} {today.prevSuccess}{!data.orderSync?.enabled && ` · 之前已失败 ${today.prevFailed}`}</div></div>
              <div><div className="mon-sub">成交</div><b>{today.won}</b><div className="mon-sub">AI 独立 {today.aiWin} · 人工介入 {today.humanWin}</div></div>
              <div><div className="mon-sub">成功率</div><b>{today.rate == null ? '—' : `${Math.round(today.rate * 100)}%`}</b><div className="mon-sub">成交 ÷ 有效进线</div></div>
            </div>
            <div className="mon-plans">
              {Object.entries(today.plans).map(([k, n]) => (
                <span key={k}>{PLAN_NAME[k]} <b>{n}</b></span>
              ))}
            </div>
            <div className="mon-sub" style={{ marginTop: 8 }}>
              今日消息：客户 {today.user_msgs} · AI {today.ai_msgs} · 人工 {today.agent_msgs}
            </div>
          </div>

          <div className="mon-card">
            <div className="mon-card-title">模型用量（今天）<b>{fmtTok(tokens)}</b> tokens</div>
            {today.llm.length ? (
              <table className="mon-table">
                <thead><tr><th>模型</th><th className="num">调用</th><th className="num">输入</th><th className="num">输出</th><th className="num">缓存命中</th></tr></thead>
                <tbody>
                  {today.llm.map(r => (
                    <tr key={r.provider + r.model}>
                      <td>{r.model}<div className="mon-sub">{r.provider}</div></td>
                      <td className="num">{r.calls}</td>
                      <td className="num">{fmtTok(r.prompt)}</td>
                      <td className="num">{fmtTok(r.completion)}</td>
                      <td className="num">{pct(r.cache_hit, r.prompt)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td>合计</td>
                    <td className="num">{llmTotal.calls}</td>
                    <td className="num">{fmtTok(llmTotal.prompt)}</td>
                    <td className="num">{fmtTok(llmTotal.completion)}</td>
                    <td className="num">{pct(llmTotal.cache_hit, llmTotal.prompt)}</td>
                  </tr>
                </tfoot>
              </table>
            ) : <div className="mon-sub">今天还没有调用</div>}
          </div>
        </div>
      </section>

      {data.orderSync?.enabled && (syncErrors.length > 0 || data.orderSync.workerError || retrying || retryResult) && (
        <details className="mon-sync-panel">
          <summary className="mon-sync-summary">
            <span className="mon-layer-name">订单同步待处理</span>
            <span className="mon-sub">{syncErrors.length} 项{data.orderSync.workerError && ' · 工作线程异常'}{retrying && ' · 正在提交重试'}</span>
          </summary>
          <div className="mon-layer mon-sync-body">
            <div className="mon-sync-head">
              <div className="mon-sub">失败任务可手动重试</div>
              <button className="btn btn-sm mon-sync-retry-all" disabled={!!retrying || !retryableJobs.length}
                title="仅重试当前列表中的失败任务，等待自动重试的任务不重复提交"
                onClick={() => retrySync(retryableJobs)}>
                {retrying ? `提交中 ${retrying.done}/${retrying.total}` : `一键重试（${retryableJobs.length}）`}
              </button>
            </div>
            <div className="mon-sub">仅重试当前列表中的失败任务（最多 100 项），等待重试的任务由系统自动处理。</div>
            {retryResult && <div className="mon-sync-result mon-sub" role="status">
              已提交 {retryResult.submitted} 项重试{retryResult.failed > 0 && `，失败 ${retryResult.failed} 项`}
              {retryResult.skipped > 0 && `，未提交 ${retryResult.skipped} 项`}
              <span>。提交仅表示重新排队，最终结果以同步状态为准。</span>
              {retryResult.stopped && <div>批量重试已停止，请确认登录、权限或网络状态后再操作。网络中断时当前项可能已入队，请先刷新核实，勿直接重复提交。</div>}
              {retryResult.failures.length > 0 && <details><summary>查看提交失败原因</summary>
                <ul>{retryResult.failures.map(f => <li key={f.id}>{f.message}</li>)}</ul>
              </details>}
              {retryResult.refreshError && <div className="err">重试结果已保留，刷新失败：{retryResult.refreshError}</div>}
            </div>}
            {data.orderSync.workerError && <div role="alert" className="err">{data.orderSync.workerError.message}</div>}
            {syncErrors.length > 0 && <div style={{ overflowX: 'auto' }}>
              <table className="mon-table">
                <thead><tr><th>订单</th><th>任务</th><th>异常</th><th>状态</th></tr></thead>
                <tbody>{syncErrors.map(e => <tr key={e.id}>
                  <td>{e.plate || e.order_id || '待绑定'}</td>
                  <td>{{ lookup: '归属查询', payment: '支付核验', reconcile: '状态对账', remark: '失败备注', owner: '归属同步' }[e.kind] || e.kind}</td>
                  <td style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{e.error_message}</td>
                  <td>{e.state === 'retry' ? '等待重试' : e.state === 'failed' && e.id ? <button className="btn btn-sm"
                    disabled={!!retrying} onClick={() => retrySync([e], true)}>{retrying?.id === e.id ? '提交中' : '重试'}</button> : '处理中'}</td>
                </tr>)}</tbody>
              </table>
            </div>}
          </div>
        </details>
      )}

      {stale && <div className="mon-stale-banner">数据已 {staleSec} 秒没更新，下面显示的可能是旧的</div>}
    </div>
  )
}

function Kpi({ name, value, sub, tone = '' }) {
  return (
    <div className={`mon-kpi ${tone}`}>
      <div className="mon-sub">{name}</div>
      <div className="mon-kpi-val">{value}</div>
      <div className="mon-sub">{sub}</div>
    </div>
  )
}
